<template>
  <div class="operation-report-show-body-form-item flex flex-col">
    <div
      class="form-item-label aisea-text-16-700-dark-gray"
      :class="{
        required: required,
        'is-editing': isEditing,
        'has-errors': hasErrors,
      }"
    >
      {{ label }}
    </div>

    <div class="form-item-value-unit-edit flex aisea-text-16-400-dark-gray">
      <div
        class="form-item-value-edit w-full flex items-center date-range-fields"
      >
        <input
          type="date"
          v-model="start"
          :disabled="disabled"
          class="date-range-input"
        />
        <span class="date-range-sep">〜</span>
        <input
          type="date"
          v-model="end"
          :disabled="disabled"
          class="date-range-input"
        />
        <input
          class="validate-hidden-input"
          type="hidden"
          :name="name"
          v-model="value"
          v-validate="vValidate"
          :disabled="disabled"
        />
      </div>
    </div>
    <div
      class="nullable-label flex items-center aisea-text-16-400-dark-gray"
      v-if="hasNullableLabel"
    >
      <vs-checkbox
        v-model="nullableChecked"
        :color="checkBoxColor"
        :disabled="disabled"
        @change="onChange"
      >
        <span>{{ nullableLabel }}</span>
      </vs-checkbox>
    </div>
  </div>
</template>

<script>
import isEmpty from "lodash/isEmpty";
import isString from "lodash/isString";
import isObject from "lodash/isObject";
import { ColorMixin } from "@/mixins";

const { AISEA_COLOR } = ColorMixin.data();

const EVENTS = { CHANGE: "change" };

// DATE_RANGE_PICKER(14) の値: value_json { start: "YYYY-MM-DD", end: "YYYY-MM-DD" }
export default {
  name: "FormItemDateRangePicker",
  inheritAttrs: false,
  props: {
    label: { type: String, default: "" },
    required: { type: Boolean, default: false },
    name: { type: String, required: true },
    initialValue: { type: [Object, String], required: false },
    unit: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
    isEditing: { type: Boolean, default: false },
    nullableLabel: { type: String, required: false },
    isNullableChecked: { type: Boolean, default: false },
    hasReportFormVersionItemValue: { type: Boolean, default: false },
    defaultValue: { type: [Object, String], required: false },
  },
  data() {
    return {
      start: "",
      end: "",
      value: "", // validation 用シリアライズ値
      nullableChecked: false,
    };
  },
  watch: {
    initialValue: {
      handler() {
        this.applyInitialValue();
      },
      immediate: true,
      deep: true,
    },
    isNullableChecked: {
      handler() {
        this.nullableChecked = this.isNullableChecked;
      },
      immediate: true,
    },
    start() {
      this.recompute();
    },
    end() {
      this.recompute();
    },
    vValidate() {
      this.$nextTick(async () => {
        await this.$validator.validateAll();
      });
    },
  },
  computed: {
    emittedValue() {
      const start = this.start || "";
      const end = this.end || "";
      if (!start && !end) return null;
      return { start, end };
    },
    hasUnit() {
      return isString(this.unit) && !isEmpty(this.unit);
    },
    isNullable() {
      if (!this.hasNullableLabel) return !this.required;
      return this.nullableChecked === true;
    },
    vValidate() {
      return this.required && !this.isNullable ? "required" : "";
    },
    hasErrors() {
      return this.errors.has(this.name);
    },
    hasNullableLabel() {
      return isString(this.nullableLabel) && !isEmpty(this.nullableLabel);
    },
    checkBoxColor() {
      return AISEA_COLOR.BLUE;
    },
  },
  methods: {
    parseValue(v) {
      if (isObject(v)) return v;
      if (isString(v) && v !== "") {
        try {
          return JSON.parse(v);
        } catch {
          return null;
        }
      }
      return null;
    },
    applyInitialValue() {
      const v = this.parseValue(this.initialValue);
      this.start = v && v.start ? v.start : "";
      this.end = v && v.end ? v.end : "";
      this.value = this.start || this.end ? `${this.start}~${this.end}` : "";
    },
    recompute() {
      this.value = this.start || this.end ? `${this.start}~${this.end}` : "";
      this.onChange();
    },
    onChange() {
      if (this.disabled) return;
      this.$emit(EVENTS.CHANGE, {
        value: this.emittedValue,
        nullableChecked: this.nullableChecked,
      });
    },
  },
};
</script>
<style lang="scss" scoped>
@import "@/assets/scss/aisea/_variables.scss";

.operation-report-show-body-form-item {
  min-height: 90px;
  padding: 18px 0;
  row-gap: 4px;

  .form-item-value-unit-edit {
    gap: 18px;
    padding-right: 18px;
  }

  .date-range-fields {
    gap: 12px;
  }

  .date-range-input {
    padding: 10px 12px;
    border: 1px solid $aisea-light-gray;
    border-radius: 6px;
    font-size: 16px;
  }

  .date-range-sep {
    flex-shrink: 0;
  }

  .form-item-label.is-editing {
    &.has-errors {
      color: $aisea-red;
    }
    &.required:after {
      content: " *";
      font-size: 14px;
      color: $aisea-red;
      font-weight: 700;
      font-family: $aisea-font-family;
    }
  }
  .nullable-label {
    gap: 20px;
  }
}
</style>
